import { type Socket, io } from 'socket.io-client'
import { ref } from 'vue'
import { i18n } from '@/plugins/i18n' // ✅ Import i18n instance โดยตรง
import { useToast } from '@/composables/useToast'
import { socketLogger } from '@/utils/logger'
import { invalidateCache, invalidateCachePattern } from '@/composables/useApiCache'
import { dispatchCustomEvent } from '@/utils/events'

// ✅ TypeScript global declaration
declare global {
  interface Window {
    __socketUnloadHandlerSetup?: boolean
  }
}

// สร้างตัวแปร socket นอก composable เพื่อให้เป็น Singleton
let socket: Socket | null = null
const socketRef = ref<Socket | null>(null)
const isConnected = ref(false)
const connectionError = ref<string | null>(null)

export const useSocket = () => {
  const { showToast } = useToast()

  // ✅ ใช้ i18n.global.t แทน useI18n เพราะทำงาน outside component context
  const t = (key: string) => i18n.global.t(key)

  /**
   * สร้างการเชื่อมต่อ WebSocket ไปยัง Server
   * @param token - JWT Access Token
   */
  const connectSocket = (token: string) => {
    // ตรวจสอบว่าเป็น temporary token หรือไม่
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))

      if (payload.isTemporary) {
        socketLogger.debug('Temporary token detected, skipping WebSocket connection')

        return
      }
    }
    catch (error) {
      socketLogger.error('Error parsing token:', error)

      return
    }

    // ถ้าเชื่อมต่ออยู่แล้ว แต่ token เปลี่ยน ต้อง reconnect
    if (socket?.connected) {
      const currentToken = typeof socket.auth === 'object' && socket.auth !== null ? (socket.auth as any).token : null
      if (currentToken === token) {
        socketLogger.debug('Socket already connected with same token, reusing existing connection')

        return
      }

      socketLogger.debug('Token changed, need to reconnect WebSocket')
    }

    // ตัดการเชื่อมต่อเก่า (ถ้ามี) ก่อนสร้างใหม่
    if (socket) {
      socketLogger.debug('Disconnecting old socket before creating new one')
      socket.removeAllListeners() // ✅ ลบ event listeners เก่าเพื่อป้องกัน memory leak
      socket.disconnect()
    }

    const URL = import.meta.env.VITE_SOCKET_URL || import.meta.env.VITE_API_URL || 'http://localhost:3000'

    socketLogger.debug('Connecting to WebSocket server:', URL)

    socket = io(URL, {
      // ส่ง token ไปใน handshake เพื่อให้ Backend ตรวจสอบสิทธิ์
      auth: {
        token,
      },

      // ✅ Optimized reconnection config
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000, // Start with 1 second
      reconnectionDelayMax: 3000, // Max 3 seconds (optimized from 5s)
      timeout: 20000,

      // ✅ Prefer WebSocket for faster reconnection
      transports: ['websocket', 'polling'],
    })

    // อัปเดต socketRef เพื่อให้ reactive
    socketRef.value = socket

    // ✅ Connect event handler
    socket.on('connect', () => {
      socketLogger.info('Socket connected:', socket?.id)

      // ✅ เช็คก่อน set null เพื่อแสดง toast reconnect success
      const wasDisconnected = connectionError.value !== null

      isConnected.value = true
      connectionError.value = null

      // แจ้งเตือนผู้ใช้ (ถ้าเคย disconnect)
      if (wasDisconnected) {
        showToast({
          type: 'success',
          message: t('socket.connected'),
        })
      }
    })

    // ✅ Disconnect event handler
    socket.on('disconnect', reason => {
      socketLogger.info('Socket disconnected:', reason)
      isConnected.value = false

      if (reason === 'io server disconnect') {
        // เซิร์ฟเวอร์บังคับตัดการเชื่อมต่อ - ไม่ต้อง reconnect
        socketLogger.debug('Server forcefully disconnected, not reconnecting')
      }
      else if (reason === 'transport close' || reason === 'transport error') {
        // Network issue - จะ reconnect อัตโนมัติ
        socketLogger.debug('Network issue, will attempt to reconnect...')
        connectionError.value = t('socket.disconnected')
      }
    })

    // ✅ Connect error handler
    socket.on('connect_error', error => {
      socketLogger.error('Connection error:', error.message)
      isConnected.value = false
      connectionError.value = `${t('socket.connectionError')}: ${error.message}`

      // Note: ไม่แสดง toast ที่นี่ เพราะจะแสดงใน reconnect_failed แทน
      // เพื่อป้องกัน toast spam ระหว่าง reconnect
    })

    // ✅ Reconnect attempt handler
    socket.io.on('reconnect_attempt', attempt => {
      socketLogger.debug(`Reconnection attempt ${attempt}...`)
      connectionError.value = `${t('socket.reconnectAttempt')} ${attempt}...`
    })

    // ✅ Reconnect error handler
    socket.io.on('reconnect_error', error => {
      socketLogger.error('Reconnection error:', error.message)
      connectionError.value = `${t('socket.reconnectError')}: ${error.message}`
    })

    // ✅ Reconnect failed handler
    socket.io.on('reconnect_failed', () => {
      socketLogger.error('Reconnection failed after all attempts')
      connectionError.value = t('socket.connectionFailed')

      showToast({
        type: 'error',
        message: t('socket.connectionFailed'),
        duration: 0, // แสดงตลอด
      })
    })

    // ✅ Reconnect success handler (ไม่แสดง toast เพราะ 'connect' handler จัดการแล้ว)
    socket.io.on('reconnect', attemptNumber => {
      socketLogger.info(`Reconnected successfully after ${attemptNumber} attempts`)

      // ไม่ต้อง set state ที่นี่ เพราะ 'connect' event จะ fire หลังจากนี้
    })

    // ✅ Force Logout handler
    socket.on('force_logout', () => {
      socketLogger.info('Received force_logout event from server')

      // Dispatch event ให้ auth store จัดการเอง (ป้องกัน circular dependency)
      dispatchCustomEvent('force_logout')
    })

    // ✅ User Data Updated handler (Real-time sync when admin updates user data)
    socket.on('user-data-updated', (data: any) => {
      socketLogger.info('User profile updated by administrator:', data)

      // Dispatch event ให้ auth store refresh user data
      dispatchCustomEvent('user-data-updated', data)

      // แจ้งเตือนผู้ใช้
      // showToast({
      //   type: 'info',
      //   message: data.message || 'Your profile has been updated',
      // })
    })

    // ✅ New Notification handler
    socket.on('newNotification', notification => {
      socketLogger.debug('New notification received:', notification)

      // Dispatch custom event ให้ NavBarNotifications ฟัง
      dispatchCustomEvent('newNotification', notification)

      // ✅ Toast is handled by NavBarNotifications.vue to prevent duplicates and apply correct broadcast message types
    })

    // ✅ Notification Update handler
    socket.on('notificationUpdate', notification => {
      socketLogger.debug('Notification updated:', notification)

      // Dispatch custom event ให้ NavBarNotifications ฟัง
      dispatchCustomEvent('notificationUpdate', notification)
    })

    // ✅ Notification Read handler
    socket.on('notificationRead', data => {
      socketLogger.debug('Notification marked as read:', data)

      // Dispatch custom event ให้ NavBarNotifications ฟัง
      dispatchCustomEvent('notificationRead', data)
    })

    // ✅ User Status Changed handler
    socket.on('userStatusChanged', data => {
      socketLogger.debug('User status changed:', data)

      // Dispatch event for dashboard and other components
      dispatchCustomEvent('userStatusChanged', data)
    })

    // ✅ Cache Invalidation handler (WebSocket Cache Sync)
    socket.on('cache:invalidate', ({ pattern, keys, timestamp }) => {
      socketLogger.debug('Cache invalidation received:', { pattern, keys, timestamp })

      // Invalidate by pattern
      if (pattern)
        invalidateCachePattern(pattern)

      // Invalidate specific keys
      if (keys && Array.isArray(keys))
        keys.forEach((key: string) => invalidateCache(key))

      // Show notification in development mode
    })

    // ✅ User Stats Updated handler
    socket.on('user_stats_updated', response => {
      socketLogger.debug('User stats updated:', response)

      // ✅ Handle success/error response format
      if (response.success) {
        // Dispatch event for dashboard
        window.dispatchEvent(new CustomEvent('user_stats_updated', {
          detail: response.data,
        }))
      }
      else {
        socketLogger.error('Failed to fetch user stats:', response.error)

        // แจ้งเตือน error ให้ผู้ใช้ทราบ
        showToast({
          type: 'error',
          message: t('socket.fetchUserStatsError'),
        })
      }
    })

    // ✅ Role Permissions Updated handler
    socket.on('rolePermissionsUpdated', data => {
      socketLogger.info('Role permissions updated:', data)

      // Dispatch event for auth store to refresh user data
      window.dispatchEvent(new CustomEvent('rolePermissionsUpdated', {
        detail: data,
      }))

      // Show toast notification
      showToast({
        type: 'info',
        message: data.message || t('socket.permissionsUpdated'),
      })
    })
  }

  const disconnectSocket = () => {
    if (socket) {
      socketLogger.info('Disconnecting socket...')
      socket.disconnect()
      socket = null
      socketRef.value = null
      isConnected.value = false
      connectionError.value = null
    }
  }

  // ✅ Setup page unload handler to disconnect immediately when browser closes
  // Only setup once per useSocket instance
  if (typeof window !== 'undefined' && !window.__socketUnloadHandlerSetup) {
    // Handle page unload (browser close, tab close, navigation)
    const handleBeforeUnload = () => {
      if (socket?.connected) {
        socketLogger.debug('Browser closing - disconnecting socket immediately')

        // Send disconnect immediately without waiting
        socket.disconnect()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    // Mark as setup to prevent duplicate listeners
    window.__socketUnloadHandlerSetup = true
  }

  return {
    socket: socketRef,
    isConnected,
    connectionError,
    connectSocket,
    disconnectSocket,
  }
}
